import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface TeamMember {
  id: string;
  name: string;
  title: string;
  specialty: string;
  bio: string;
  certifications: string[];
  languages: string[];
  imageUrl: string;
}

interface TeamMemberEditorProps {
  initialMembers: TeamMember[];
  onSave: (members: TeamMember[]) => void;
}

const ALL_LANGUAGES = [
  { code: 'DE', flag: '🇩🇪', labelKey: 'adm2.tm_lang_de' },
  { code: 'IT', flag: '🇮🇹', labelKey: 'adm2.tm_lang_it' },
  { code: 'EN', flag: '🇬🇧', labelKey: 'adm2.tm_lang_en' },
  { code: 'FR', flag: '🇫🇷', labelKey: 'adm2.tm_lang_fr' },
  { code: 'ES', flag: '🇪🇸', labelKey: 'adm2.tm_lang_es' },
  { code: 'NL', flag: '🇳🇱', labelKey: 'adm2.tm_lang_nl' },
  { code: 'PL', flag: '🇵🇱', labelKey: 'adm2.tm_lang_pl' },
  { code: 'AL', flag: '🇦🇱', labelKey: 'adm2.tm_lang_al' },
];

const emptyMember: TeamMember = {
  id: '',
  name: '',
  title: '',
  specialty: '',
  bio: '',
  certifications: [],
  languages: [],
  imageUrl: '',
};

export default function TeamMemberEditor({ initialMembers, onSave }: TeamMemberEditorProps) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<TeamMember[]>(initialMembers);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState<TeamMember>(emptyMember);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploadError(null);
    setIsUploading(true);

    if (file.size > 5 * 1024 * 1024) {
      setImageUploadError(t('adm2.file_too_large_compress'));
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (!file.type.startsWith('image/')) {
      setImageUploadError(t('adm2.only_image_files'));
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target?.result as string;
      if (base64) {
        updateDraft('imageUrl', base64);
        setImageUploadError(null);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 2000);
      }
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.onerror = () => {
      setImageUploadError(t('adm2.file_read_error_smaller'));
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const startEdit = (member: TeamMember) => {
    setDraft({ ...member });
    setEditingId(member.id);
    setIsAdding(false);
    setDeleteConfirm(null);
  };

  const startAdd = () => {
    setDraft({
      ...emptyMember,
      id: 'member-' + Date.now(),
      certifications: [''],
      languages: ['DE'],
    });
    setIsAdding(true);
    setEditingId(null);
    setDeleteConfirm(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    setDraft(emptyMember);
    setDeleteConfirm(null);
  };

  const saveDraft = () => {
    if (!draft.name.trim()) return;
    if (isAdding) {
      setMembers((prev) => [...prev, draft]);
    } else {
      setMembers((prev) => prev.map((m) => (m.id === draft.id ? draft : m)));
    }
    onSave(isAdding ? [...members, draft] : members.map((m) => (m.id === draft.id ? draft : m)));
    setEditingId(null);
    setIsAdding(false);
    setDraft(emptyMember);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleDelete = (id: string) => {
    const next = members.filter((m) => m.id !== id);
    setMembers(next);
    onSave(next);
    setDeleteConfirm(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const updateDraft = (field: keyof TeamMember, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateCertifications = (index: number, value: string) => {
    setDraft((prev) => {
      const certs = [...prev.certifications];
      certs[index] = value;
      return { ...prev, certifications: certs };
    });
  };

  const addCertification = () => {
    setDraft((prev) => ({ ...prev, certifications: [...prev.certifications, ''] }));
  };

  const removeCertification = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  const toggleLanguage = (code: string) => {
    setDraft((prev) => {
      const has = prev.languages.includes(code);
      return {
        ...prev,
        languages: has ? prev.languages.filter((l) => l !== code) : [...prev.languages, code],
      };
    });
  };

  const isEditing = editingId !== null || isAdding;

  return (
    <div className="space-y-6">
      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
          <span className="text-xs text-green-700 font-body">{t('adm2.saved_live')}</span>
        </div>
      )}

      {/* Member List */}
      {!isEditing && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="font-label text-xs uppercase tracking-[0.15em] text-foreground-500">
              {t('adm2.tm_members_count', { count: members.length })}
            </h2>
            <button
              onClick={startAdd}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-sm"></i>
              {t('adm2.tm_add_member')}
            </button>
          </div>

          {members.length === 0 && (
            <div className="text-center py-12 bg-background-50 rounded-xl border border-background-200/70">
              <i className="ri-team-line text-3xl text-foreground-300"></i>
              <p className="mt-2 text-sm text-foreground-500 font-body">{t('adm2.tm_no_members')}</p>
              <button
                onClick={startAdd}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm"></i>
                {t('adm2.tm_add_first_member')}
              </button>
            </div>
          )}

          {members.map((member) => (
            <div
              key={member.id}
              className="bg-background-50 rounded-xl border border-background-200/70 p-4 flex items-center gap-4 hover:border-primary-200 transition-colors"
            >
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                {member.imageUrl ? (
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-user-line text-lg text-foreground-300"></i>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-label text-sm font-semibold text-foreground-900 truncate">{member.name}</h3>
                <p className="text-xs text-foreground-500 font-body truncate">{member.title}</p>
                <div className="flex items-center gap-1 mt-1">
                  {member.languages.slice(0, 3).map((lang) => (
                    <span key={lang} className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 border border-background-200 text-foreground-500 font-label">
                      {lang}
                    </span>
                  ))}
                  {member.languages.length > 3 && (
                    <span className="text-[10px] text-foreground-400 font-label">+{member.languages.length - 3}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => startEdit(member)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                  title={t('adm2.edit')}
                >
                  <i className="ri-pencil-line text-sm"></i>
                </button>
                {deleteConfirm === member.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="px-2 py-1 rounded bg-red-500 text-white text-[10px] font-label hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('adm2.delete')}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1 rounded bg-background-200 text-foreground-600 text-[10px] font-label hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('adm2.cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(member.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                    title={t('adm2.delete')}
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Form */}
      {isEditing && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-label text-sm font-semibold text-foreground-900">
              {isAdding ? t('adm2.tm_new_member') : t('adm2.tm_edit_member')}
            </h2>
            <button
              onClick={cancelEdit}
              className="text-xs text-foreground-500 hover:text-foreground-700 font-label cursor-pointer"
            >
              {t('adm2.cancel')}
            </button>
          </div>

          {/* Image Preview + Upload */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
              {t('adm2.tm_profile_image')}
            </label>
            <div className="flex items-start gap-4">
              <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                {draft.imageUrl ? (
                  <>
                    <img
                      src={draft.imageUrl}
                      alt={t('adm2.preview')}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    {uploadSuccess && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-[10px] font-label bg-green-500 px-2 py-0.5 rounded-full">
                          {t('adm2.uploaded')}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-foreground-300"></i>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setTimeout(() => {
                      fileInputRef.current?.click();
                    }, 0);
                  }}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-background-300 bg-background-50 transition-colors cursor-pointer whitespace-nowrap ${isUploading ? 'opacity-60 pointer-events-none' : 'hover:bg-background-100'}`}
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-foreground-600 font-label">{t('adm2.uploading_progress')}</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-cloud-2-line text-foreground-400"></i>
                      <span className="text-xs text-foreground-600 font-label">{t('adm2.tm_upload_image_max')}</span>
                    </>
                  )}
                </button>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <div className="relative">
                  <input
                    type="text"
                    value={draft.imageUrl}
                    onChange={(e) => updateDraft('imageUrl', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-[11px] text-foreground-900 font-mono focus:outline-none focus:border-primary-400 transition-all"
                    placeholder={t('adm2.enter_url')}
                  />
                </div>
                {imageUploadError && (
                  <p className="text-[11px] text-red-500 font-body">{imageUploadError}</p>
                )}
                <p className="text-[9px] text-foreground-400 font-body">
                  {t('adm2.tm_image_hint')}
                </p>
              </div>
            </div>
          </div>

          {/* Name & Title */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
                {t('adm2.tm_name')} *
              </label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => updateDraft('name', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                placeholder={t('adm2.tm_name_ph')}
              />
            </div>
            <div>
              <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
                {t('adm2.tm_title_position')}
              </label>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => updateDraft('title', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                placeholder={t('adm2.tm_title_ph')}
              />
            </div>
          </div>

          {/* Specialty */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
              {t('adm2.tm_specialty')}
            </label>
            <input
              type="text"
              value={draft.specialty}
              onChange={(e) => updateDraft('specialty', e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
              placeholder={t('adm2.tm_specialty_ph')}
            />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
              {t('adm2.tm_bio')}
            </label>
            <textarea
              value={draft.bio}
              onChange={(e) => updateDraft('bio', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body leading-relaxed focus:outline-none focus:border-primary-400 transition-all resize-y"
              placeholder={t('adm2.tm_bio_ph')}
            />
          </div>

          {/* Languages */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
              {t('adm2.tm_languages')}
            </label>
            <div className="flex flex-wrap gap-2">
              {ALL_LANGUAGES.map((lang) => {
                const isSelected = draft.languages.includes(lang.code);
                return (
                  <button
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                      isSelected
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200 border border-background-200'
                    }`}
                  >
                    {lang.flag} {t(lang.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Certifications */}
          <div>
            <label className="block text-xs tracking-[0.1em] uppercase font-label text-foreground-600 mb-2">
              {t('adm2.tm_certifications')}
            </label>
            <div className="space-y-2">
              {draft.certifications.map((cert, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={cert}
                    onChange={(e) => updateCertifications(index, e.target.value)}
                    className="flex-1 px-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                    placeholder={t('adm2.tm_cert_ph', { num: index + 1 })}
                  />
                  <button
                    onClick={() => removeCertification(index)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                    title={t('adm2.remove')}
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ))}
              <button
                onClick={addCertification}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-background-300 text-xs text-foreground-500 font-label hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line text-sm"></i>
                {t('adm2.tm_add_cert')}
              </button>
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-2 border-t border-background-200">
            <button
              onClick={saveDraft}
              disabled={!draft.name.trim()}
              className="w-full py-3 rounded-xl bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label font-semibold hover:bg-primary-600 disabled:bg-foreground-200 disabled:text-foreground-400 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {isAdding ? t('adm2.tm_add_member') : t('adm2.save_changes')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}